// ABOUTME: Loads the business's one in-app Hermes conversation from
// GET /agent/chat/history (#2569 Part 1) for the chat surface's mount-time
// bootstrap. Replaces the old session-scoped, paginated GET /agent/history
// restore (historyRestore.ts): the new endpoint returns a flat, oldest-first
// page with no offset/session_id — there is no "load earlier messages" or
// per-session deep-link restore any more, since the backend has one
// conversation per business rather than many.

import { getChatHistory, type AgentChatHistoryMessage, type PlatformSource } from '@/api/agent'
import type { AIChatAttachment, AIChatMessage } from '@/stores/aiChatTypes'

// The line POST /agent/chat folds each attachment into the stored message
// as (app/api/v1/agent/chat.py `_merge_attachments`).
const ATTACHMENT_LINE = /^\[attachment: (.+) \(([\w.+-]+\/[\w.+-]+)\) — (\S+)\]$/
// The line a document kept in Files is folded in as (app/services/files/inbound.py).
const DOCUMENT_LINE = /^\[document saved to Files: (.+) \(file_id ([\w-]+)\)\]$/

/** Split folded attachment lines back out of a stored message. */
function splitAttachments(content: string): {
  text: string
  attachments: AIChatAttachment[]
} {
  const attachments: AIChatAttachment[] = []
  const kept: string[] = []
  for (const line of content.split('\n')) {
    const match = ATTACHMENT_LINE.exec(line)
    const document = DOCUMENT_LINE.exec(line)
    if (match) {
      attachments.push({ filename: match[1], content_type: match[2], url: match[3], size_bytes: 0 })
    } else if (document) {
      attachments.push({
        filename: document[1],
        content_type: '',
        url: '',
        size_bytes: 0,
        file_id: document[2],
      })
    } else {
      kept.push(line)
    }
  }
  return { text: kept.join('\n').trim(), attachments }
}

/** Each brief's team member, by brief id, from the member's own posts (named by their label). */
function briefMembers(messages: AgentChatHistoryMessage[]): Map<string, string> {
  const members = new Map<string, string>()
  for (const m of messages) {
    if (m.direction === 'outbound' && m.production_brief_id && m.sender_label) {
      members.set(m.production_brief_id, m.sender_label)
    }
  }
  return members
}

/** Map the wire shape (`direction`) to the transcript's own (`role`). */
function toChatMessage(
  message: AgentChatHistoryMessage,
  members: Map<string, string>
): AIChatMessage {
  if (message.platform_request) {
    // The card shows the request itself; the stored text is the lead's briefing.
    return {
      id: message.id,
      role: 'assistant',
      content: '',
      timestamp: message.created_at,
      metadata: { platform_request: message.platform_request },
    }
  }
  if (message.production_brief) {
    // The card reads the brief live; the stored text is its one-line summary.
    return {
      id: message.id,
      role: 'assistant',
      content: '',
      timestamp: message.created_at,
      metadata: { production_brief: message.production_brief },
    }
  }
  const { text, attachments } = splitAttachments(message.content)
  const briefId = message.production_brief_id
  const member = briefId ? members.get(briefId) : undefined
  return {
    id: message.id,
    role: message.direction === 'inbound' ? 'user' : 'assistant',
    content: text,
    timestamp: message.created_at,
    ...(attachments.length > 0 ? { attachments } : {}),
    ...(briefId && member
      ? { metadata: { team_member: { name: member, brief_id: briefId } } }
      : {}),
  }
}

function toChatMessages(messages: AgentChatHistoryMessage[]): AIChatMessage[] {
  const members = briefMembers(messages)
  return messages.map((m) => toChatMessage(m, members))
}

/** Fetch the conversation's most recent messages, oldest first. */
export async function loadHermesChatHistory(limit = 50): Promise<AIChatMessage[]> {
  const history = await getChatHistory(limit)
  return toChatMessages(history.messages)
}

/** The thread filtered to `source` (omit for everything) plus the platforms it can be filtered by. */
export async function loadHermesThread(
  limit: number,
  source?: string
): Promise<{ messages: AIChatMessage[]; sources: PlatformSource[] }> {
  const history = await getChatHistory(limit, source)
  return { messages: toChatMessages(history.messages), sources: history.platform_sources ?? [] }
}
